import { useState, useEffect, useRef } from "react";
import { useSearch } from "wouter";
import { Shield, Phone, Mail, CreditCard, Calendar, ChevronRight, Lock, HelpCircle, AlertCircle, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";

// Unifi branding colors
const BRAND = {
  bg: "#0a0f1a",
  card: "#111827",
  border: "#1f2937",
  accent: "#3b82f6",
  accentLight: "#60a5fa",
  gold: "#f59e0b",
  green: "#10b981",
  text: "#f9fafb",
  textMuted: "#9ca3af",
  textDim: "#6b7280",
};

interface AccountInfo {
  id: number;
  accountNumber: string;
  debtorName: string;
  originalBalance: string;
  currentBalance: string;
  email?: string;
  phonePrimary?: string;
  status: string;
  settlementAmount?: string;
  totalCollected: string;
  placementId: number;
}

interface PaymentOption {
  id: string;
  label: string;
  description: string;
  amount: number;
  savings: number;
  savingsPercent: number;
  months: number;
  monthlyAmount?: number;
  badge?: string;
  recommended?: boolean;
}

function generatePaymentOptions(currentBalance: number): PaymentOption[] {
  const sif50 = currentBalance * 0.50;
  const sif60 = currentBalance * 0.60;
  const sixMonth = currentBalance * 0.65;
  const twelveMonth = currentBalance * 0.75;

  return [
    {
      id: "sif_50",
      label: "Biggest Savings",
      description: "One-time settlement payment",
      amount: Math.round(sif50 * 100) / 100,
      savings: currentBalance - sif50,
      savingsPercent: 50,
      months: 1,
      badge: "Save 50%",
    },
    {
      id: "sif_60",
      label: "Pay It Off Fast",
      description: "One-time settlement payment",
      amount: Math.round(sif60 * 100) / 100,
      savings: currentBalance - sif60,
      savingsPercent: 40,
      months: 1,
      badge: "Save 40%",
    },
    {
      id: "plan_6",
      label: "6-Month Plan",
      description: "Monthly payments over 6 months",
      amount: Math.round(sixMonth * 100) / 100,
      savings: currentBalance - sixMonth,
      savingsPercent: 35,
      months: 6,
      monthlyAmount: Math.round((sixMonth / 6) * 100) / 100,
      badge: "Most Popular",
      recommended: true,
    },
    {
      id: "plan_12",
      label: "Lowest Monthly",
      description: "Monthly payments over 12 months",
      amount: Math.round(twelveMonth * 100) / 100,
      savings: currentBalance - twelveMonth,
      savingsPercent: 25,
      months: 12,
      monthlyAmount: Math.round((twelveMonth / 12) * 100) / 100,
    },
  ];
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);
}

export default function PaymentPortal() {
  const searchString = useSearch();
  const params = new URLSearchParams(searchString);
  const token = params.get("token");

  const [step, setStep] = useState<"loading" | "account" | "options" | "confirm" | "redirecting" | "error">("loading");
  const [account, setAccount] = useState<AccountInfo | null>(null);
  const [options, setOptions] = useState<PaymentOption[]>([]);
  const [selectedOption, setSelectedOption] = useState<PaymentOption | null>(null);
  const [error, setError] = useState<string>("");
  const [showFaq, setShowFaq] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const [osbpData, setOsbpData] = useState<{ osbpUrl: string; paymentRequest: string } | null>(null);

  // Load account from token
  useEffect(() => {
    if (!token) {
      setError("Invalid payment link. Please use the link provided in your SMS or email.");
      setStep("error");
      return;
    }

    async function loadAccount() {
      try {
        const res = await fetch(`/api/paywire/osbp/account?token=${encodeURIComponent(token!)}`);
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || "Account not found");
        }
        const data = await res.json();
        setAccount(data);
        const balance = parseFloat(data.currentBalance);
        setOptions(generatePaymentOptions(balance));
        setStep("account");
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load account information");
        setStep("error");
      }
    }

    loadAccount();
  }, [token]);

  // Auto-submit OSBP form when data is ready
  useEffect(() => {
    if (osbpData && formRef.current) {
      formRef.current.submit();
    }
  }, [osbpData]);

  const handleSelectOption = (option: PaymentOption) => {
    setSelectedOption(option);
    setStep("confirm");
  };

  const handleConfirmPayment = async () => {
    if (!selectedOption || !account) return;

    setStep("redirecting");

    try {
      // Determine the first payment amount
      const firstPaymentAmount = selectedOption.months > 1
        ? selectedOption.monthlyAmount!
        : selectedOption.amount;

      const nameParts = account.debtorName.split(" ");
      const firstName = nameParts[0] || "Customer";
      const lastName = nameParts.slice(1).join(" ") || "Customer";

      const res = await fetch("/api/paywire/osbp/init", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: firstPaymentAmount,
          accountId: account.id,
          placementId: account.placementId,
          firstName,
          lastName,
          email: account.email || "",
          phone: account.phonePrimary || "",
          description: selectedOption.months > 1
            ? `Payment Plan: ${selectedOption.label} (Payment 1 of ${selectedOption.months})`
            : `Settlement: ${selectedOption.label}`,
          type: "sale",
          requestToken: selectedOption.months > 1, // Token for payment plans
          // Pass plan details for scheduling future payments
          planDetails: selectedOption.months > 1 ? {
            totalAmount: selectedOption.amount,
            months: selectedOption.months,
            monthlyAmount: selectedOption.monthlyAmount,
            optionId: selectedOption.id,
          } : undefined,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Failed to initialize payment");
      }

      const data = await res.json();
      setOsbpData({
        osbpUrl: data.osbpUrl,
        paymentRequest: data.paymentRequest,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to start payment process");
      setStep("error");
    }
  };

  // Hidden form for OSBP auto-submit
  const OsbpForm = () => {
    if (!osbpData) return null;
    return (
      <form
        ref={formRef}
        method="POST"
        action={osbpData.osbpUrl}
        style={{ display: "none" }}
      >
        <input type="hidden" name="PWREQUEST" value={osbpData.paymentRequest} />
      </form>
    );
  };

  return (
    <div className="min-h-screen" style={{ background: BRAND.bg }}>
      <OsbpForm />

      {/* Header */}
      <header className="border-b" style={{ borderColor: BRAND.border }}>
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/favicon-192x192.png" alt="Unifi" className="w-8 h-8 rounded-lg" />
            <span className="text-lg font-semibold text-white">unifi</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs" style={{ color: BRAND.textMuted }}>
            <Lock className="w-3.5 h-3.5" />
            <span>Secure Portal</span>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Error State */}
        {step === "error" && (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: "rgba(239,68,68,0.1)" }}>
              <AlertCircle className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Something Went Wrong</h2>
            <p className="mb-6" style={{ color: BRAND.textMuted }}>{error}</p>
            <div className="flex items-center justify-center gap-4 text-sm" style={{ color: BRAND.textMuted }}>
              <a href="tel:+18337365674" className="flex items-center gap-1.5 hover:text-white transition-colors">
                <Phone className="w-4 h-4" /> (833) 736-5674
              </a>
              <span>|</span>
              <a href="mailto:support@unifipro.ai" className="flex items-center gap-1.5 hover:text-white transition-colors">
                <Mail className="w-4 h-4" /> support@unifipro.ai
              </a>
            </div>
          </div>
        )}

        {/* Loading State */}
        {step === "loading" && (
          <div className="text-center py-16">
            <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4" style={{ color: BRAND.accent }} />
            <p style={{ color: BRAND.textMuted }}>Loading your account information...</p>
          </div>
        )}

        {/* Account Overview */}
        {step === "account" && account && (
          <div>
            <div className="mb-8">
              <h1 className="text-2xl font-bold text-white mb-1">Welcome, {account.debtorName.split(" ")[0]}</h1>
              <p style={{ color: BRAND.textMuted }}>Review your account and choose a resolution option below.</p>
            </div>

            {/* Account Card */}
            <div className="rounded-xl p-6 mb-8" style={{ background: BRAND.card, border: `1px solid ${BRAND.border}` }}>
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-medium" style={{ color: BRAND.textMuted }}>Account #{account.accountNumber}</span>
                <span className="text-xs px-2.5 py-1 rounded-full font-medium" style={{
                  background: account.status === "active" ? "rgba(245,158,11,0.1)" : "rgba(16,185,129,0.1)",
                  color: account.status === "active" ? BRAND.gold : BRAND.green,
                }}>
                  {account.status === "active" ? "Open Balance" : account.status}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-xs mb-1" style={{ color: BRAND.textDim }}>Current Balance</p>
                  <p className="text-3xl font-bold text-white">{formatCurrency(parseFloat(account.currentBalance))}</p>
                </div>
                <div>
                  <p className="text-xs mb-1" style={{ color: BRAND.textDim }}>Original Balance</p>
                  <p className="text-lg font-medium" style={{ color: BRAND.textMuted }}>{formatCurrency(parseFloat(account.originalBalance))}</p>
                </div>
              </div>
              {parseFloat(account.totalCollected) > 0 && (
                <div className="mt-4 pt-4" style={{ borderTop: `1px solid ${BRAND.border}` }}>
                  <p className="text-xs" style={{ color: BRAND.textDim }}>
                    Total Paid: <span className="font-medium" style={{ color: BRAND.green }}>{formatCurrency(parseFloat(account.totalCollected))}</span>
                  </p>
                </div>
              )}
            </div>

            {/* Settlement Options */}
            <h2 className="text-lg font-semibold text-white mb-4">Choose Your Resolution Option</h2>
            <div className="space-y-3 mb-8">
              {options.map((option) => (
                <button
                  key={option.id}
                  onClick={() => handleSelectOption(option)}
                  className="w-full text-left rounded-xl p-5 transition-all duration-200 hover:scale-[1.01] group"
                  style={{
                    background: BRAND.card,
                    border: `1px solid ${option.recommended ? BRAND.accent : BRAND.border}`,
                    boxShadow: option.recommended ? `0 0 20px rgba(59,130,246,0.1)` : "none",
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-white">{option.label}</span>
                        {option.badge && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider" style={{
                            background: option.recommended ? BRAND.accent : "rgba(245,158,11,0.15)",
                            color: option.recommended ? "white" : BRAND.gold,
                          }}>
                            {option.badge}
                          </span>
                        )}
                      </div>
                      <p className="text-sm" style={{ color: BRAND.textMuted }}>{option.description}</p>
                      {option.months > 1 && (
                        <p className="text-sm mt-1" style={{ color: BRAND.accentLight }}>
                          {formatCurrency(option.monthlyAmount!)} / month for {option.months} months
                        </p>
                      )}
                    </div>
                    <div className="text-right ml-4">
                      <p className="text-xl font-bold text-white">{formatCurrency(option.amount)}</p>
                      <p className="text-xs" style={{ color: BRAND.green }}>
                        Save {formatCurrency(option.savings)}
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 ml-3 opacity-40 group-hover:opacity-100 transition-opacity text-white" />
                  </div>
                </button>
              ))}
            </div>

            {/* Need Help */}
            <div className="text-center mb-8">
              <p className="text-sm" style={{ color: BRAND.textMuted }}>
                Need to discuss your options?{" "}
                <a href="tel:+18337365674" className="underline hover:text-white transition-colors" style={{ color: BRAND.accentLight }}>
                  Call (833) 736-5674
                </a>
              </p>
            </div>

            {/* FAQ Section */}
            <div className="rounded-xl p-6" style={{ background: BRAND.card, border: `1px solid ${BRAND.border}` }}>
              <button
                onClick={() => setShowFaq(!showFaq)}
                className="w-full flex items-center justify-between text-left"
              >
                <div className="flex items-center gap-2">
                  <HelpCircle className="w-5 h-5" style={{ color: BRAND.accent }} />
                  <span className="font-medium text-white">Frequently Asked Questions</span>
                </div>
                <ChevronRight className={`w-5 h-5 transition-transform ${showFaq ? "rotate-90" : ""}`} style={{ color: BRAND.textMuted }} />
              </button>
              {showFaq && (
                <div className="mt-4 space-y-4 text-sm" style={{ color: BRAND.textMuted }}>
                  <div>
                    <p className="font-medium text-white mb-1">What is a settlement?</p>
                    <p>A settlement allows you to resolve your account for less than the full balance owed. Once the agreed amount is paid, the account is considered resolved.</p>
                  </div>
                  <div>
                    <p className="font-medium text-white mb-1">Will this affect my credit score?</p>
                    <p>Resolving your account can have a positive impact on your credit profile. A settled account is generally viewed more favorably than an outstanding debt.</p>
                  </div>
                  <div>
                    <p className="font-medium text-white mb-1">Is my payment information secure?</p>
                    <p>Yes. All payments are processed through our PCI-certified payment processor. Your card information is never stored on our servers.</p>
                  </div>
                  <div>
                    <p className="font-medium text-white mb-1">What happens after I pay?</p>
                    <p>You will receive a confirmation email with your payment receipt. For settlement payments, a settlement letter will be sent within 30 days confirming your account is resolved.</p>
                  </div>
                  <div>
                    <p className="font-medium text-white mb-1">Can I dispute this debt?</p>
                    <p>Yes. You have the right to dispute this debt. Please contact us at (833) 736-5674 or email support@unifipro.ai to initiate a dispute.</p>
                  </div>
                </div>
              )}
            </div>

            {/* Legal Disclosures */}
            <div className="mt-6 text-[11px] leading-relaxed" style={{ color: BRAND.textDim }}>
              <p>
                This is an attempt to collect a debt and any information obtained will be used for that purpose.
                This communication is from a debt collector. You have the right to request validation of the debt within 30 days of receiving this notice.
                If you dispute the debt or any portion thereof, we will obtain verification and mail a copy to you.
              </p>
            </div>
          </div>
        )}

        {/* Confirm Payment */}
        {step === "confirm" && selectedOption && account && (
          <div>
            <button
              onClick={() => setStep("account")}
              className="flex items-center gap-1 text-sm mb-6 hover:text-white transition-colors"
              style={{ color: BRAND.textMuted }}
            >
              ← Back to options
            </button>

            <h1 className="text-2xl font-bold text-white mb-6">Confirm Your Payment</h1>

            <div className="rounded-xl p-6 mb-6" style={{ background: BRAND.card, border: `1px solid ${BRAND.border}` }}>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span style={{ color: BRAND.textMuted }}>Account</span>
                  <span className="text-white font-medium">#{account.accountNumber}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span style={{ color: BRAND.textMuted }}>Plan</span>
                  <span className="text-white font-medium">{selectedOption.label}</span>
                </div>
                {selectedOption.months > 1 ? (
                  <>
                    <div className="flex justify-between items-center">
                      <span style={{ color: BRAND.textMuted }}>Total Settlement</span>
                      <span className="text-white font-medium">{formatCurrency(selectedOption.amount)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span style={{ color: BRAND.textMuted }}>Monthly Payment</span>
                      <span className="text-white font-medium">{formatCurrency(selectedOption.monthlyAmount!)} x {selectedOption.months} months</span>
                    </div>
                    <div className="pt-3" style={{ borderTop: `1px solid ${BRAND.border}` }}>
                      <div className="flex justify-between items-center">
                        <span style={{ color: BRAND.textMuted }}>Today's Payment</span>
                        <span className="text-2xl font-bold text-white">{formatCurrency(selectedOption.monthlyAmount!)}</span>
                      </div>
                      <p className="text-xs mt-1" style={{ color: BRAND.textDim }}>
                        First of {selectedOption.months} monthly payments. Remaining payments will be charged automatically.
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="pt-3" style={{ borderTop: `1px solid ${BRAND.border}` }}>
                    <div className="flex justify-between items-center">
                      <span style={{ color: BRAND.textMuted }}>Amount Due Today</span>
                      <span className="text-2xl font-bold text-white">{formatCurrency(selectedOption.amount)}</span>
                    </div>
                  </div>
                )}
                <div className="flex justify-between items-center pt-2">
                  <span style={{ color: BRAND.green }}>Your Savings</span>
                  <span className="font-bold" style={{ color: BRAND.green }}>{formatCurrency(selectedOption.savings)}</span>
                </div>
              </div>
            </div>

            <Button
              onClick={handleConfirmPayment}
              className="w-full h-14 text-lg font-semibold rounded-xl"
              style={{ background: BRAND.accent }}
            >
              <CreditCard className="w-5 h-5 mr-2" />
              Proceed to Secure Payment
            </Button>

            <div className="flex items-center justify-center gap-2 mt-4 text-xs" style={{ color: BRAND.textDim }}>
              <Lock className="w-3.5 h-3.5" />
              <span>You'll be redirected to our secure payment processor</span>
            </div>

            {selectedOption.months > 1 && (
              <div className="mt-6 rounded-lg p-4" style={{ background: "rgba(59,130,246,0.05)", border: `1px solid rgba(59,130,246,0.15)` }}>
                <div className="flex items-start gap-2">
                  <Calendar className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: BRAND.accentLight }} />
                  <div className="text-sm" style={{ color: BRAND.textMuted }}>
                    <p className="font-medium text-white mb-1">Payment Plan Details</p>
                    <p>After today's payment, your card will be automatically charged {formatCurrency(selectedOption.monthlyAmount!)} each month on the same day for the remaining {selectedOption.months - 1} payments. You can cancel anytime by calling (833) 736-5674.</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Redirecting State */}
        {step === "redirecting" && (
          <div className="text-center py-16">
            <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4" style={{ color: BRAND.accent }} />
            <h2 className="text-xl font-bold text-white mb-2">Preparing Secure Payment</h2>
            <p style={{ color: BRAND.textMuted }}>Redirecting you to our secure payment processor...</p>
            <p className="text-xs mt-2" style={{ color: BRAND.textDim }}>Please do not close this window.</p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="mt-auto py-6 text-center text-xs" style={{ color: BRAND.textDim, borderTop: `1px solid ${BRAND.border}` }}>
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex items-center justify-center gap-4 mb-2">
            <a href="tel:+18337365674" className="hover:text-white transition-colors">
              <Phone className="w-3.5 h-3.5 inline mr-1" />(833) 736-5674
            </a>
            <span>|</span>
            <a href="mailto:support@unifipro.ai" className="hover:text-white transition-colors">
              <Mail className="w-3.5 h-3.5 inline mr-1" />support@unifipro.ai
            </a>
          </div>
          <p>&copy; {new Date().getFullYear()} Unifi Pro AI. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
